/**
 * Security system initialization
 * Call this early in the application lifecycle
 */

import { secureStorage } from './dataProtection';

/**
 * Initialize all security systems
 */
export function initializeSecurity(): void {
  if (typeof window === 'undefined') return;

  // Data protection is configured within the secureStorage module
  
  // Set up security event listeners
  setupSecurityEventListeners();
}

/**
 * Set up global security event listeners
 */
function setupSecurityEventListeners(): void {
  // Clear sensitive data on auth state changes (logout)
  window.addEventListener('supabase:auth:signOut', () => {
    if (typeof window !== 'undefined') {
      sessionStorage.clear();
    }
  });

  // Clear sensitive data on page visibility change (user switches tabs for extended period)
  let visibilityTimeout: NodeJS.Timeout;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Clear sensitive data after 30 minutes of inactivity
      visibilityTimeout = setTimeout(() => {
        if (typeof window !== 'undefined') {
          sessionStorage.clear();
        }
      }, 30 * 60 * 1000);
    } else {
      // User returned, cancel the timeout
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
    }
  });

  // Set up periodic security health checks
  setInterval(() => {
    performSecurityHealthCheck();
  }, 10 * 60 * 1000); // Every 10 minutes
}

/**
 * Perform periodic security health checks
 */
function performSecurityHealthCheck(): void {
  // Cleanup of expired items occurs lazily on access via secureStorage.getItem()
  // (no periodic cleanup required)
  
  // Check for security anomalies in localStorage/sessionStorage
  const suspiciousKeys = findSuspiciousStorageKeys();
  if (suspiciousKeys.length > 0) {
    // TODO(PR-3): Replace with structured logger.warn({ event: 'security_anomaly', keys: suspiciousKeys })
  }
}

/**
 * Find potentially sensitive data in regular storage
 */
function findSuspiciousStorageKeys(): string[] {
  const suspiciousKeys: string[] = [];
  const sensitivePatterns = [
    'password',
    'secret',
    'token',
    'key',
    'credential',
    'api_key',
    'auth_token',
    'customer_id',
    'bridge_customer',
    'kyc',
    'bank',
    'account'
  ];

  [localStorage, sessionStorage].forEach(storage => {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && !key.startsWith('liquid_secure_')) {
        const lowerKey = key.toLowerCase();
        if (sensitivePatterns.some(pattern => lowerKey.includes(pattern))) {
          suspiciousKeys.push(key);
        }
      }
    }
  });

  return suspiciousKeys;
}
